import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { formatCurrency } from "../Billing/billingOverview.utils";
import TenantsGeographyStateView from "./TenantsGeographyStateView";
import {
  buildAustraliaMapFeatures,
  buildCompleteStateBreakdownRows,
  buildTenantGeographyLegendSteps,
  buildTenantsListPathForState,
  formatPercent,
  formatTenantGeographySummary,
  GEOGRAPHY_MAP_METRIC_OPTIONS,
  GEOGRAPHY_MAP_METRICS,
  getGeographyMetricMax,
  AUSTRALIA_MAP_VIEWBOX,
} from "./tenantGeography.utils";

const GeographySkeleton = () => (
  <div className="rounded-xl border border-gray-200 bg-white p-4">
    <div className="mb-3 h-4 w-40 animate-pulse rounded bg-gray-200" />
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
      <div className="aspect-[8/7] animate-pulse rounded-lg bg-gray-100" />
      <div className="space-y-2">
        {Array.from({ length: 8 }).map((_, index) => (
          <div key={index} className="h-8 animate-pulse rounded bg-gray-100" />
        ))}
      </div>
    </div>
  </div>
);

const MetricToggle = ({ options, value, onChange }) => (
  <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5">
    {options.map((option) => (
      <button
        key={option.id}
        type="button"
        onClick={() => onChange(option.id)}
        className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
          value === option.id
            ? "bg-white text-gray-900 shadow-sm"
            : "text-gray-500 hover:text-gray-700"
        }`}
      >
        {option.label}
      </button>
    ))}
  </div>
);

const StateTooltip = ({ feature, mapMetric, currency, revenueAvailable }) => {
  if (!feature) {
    return null;
  }

  return (
    <div className="pointer-events-none absolute bottom-3 left-3 z-10 max-w-xs rounded-lg border border-gray-200 bg-white/95 px-3 py-2 text-xs shadow-sm backdrop-blur">
      <div className="font-semibold text-gray-900">
        {feature.code ? `${feature.code} · ${feature.name}` : feature.name}
      </div>
      <div className="mt-1 space-y-0.5 text-gray-600">
        <div>
          {feature.count} tenant{feature.count === 1 ? "" : "s"} ({formatPercent(feature.tenantSharePct)} of
          platform)
        </div>
        {feature.count ? (
          <div>
            {feature.active} active
            {feature.suspended ? ` · ${feature.suspended} suspended` : ""}
            {feature.onTrial ? ` · ${feature.onTrial} on trial` : ""}
          </div>
        ) : null}
        {revenueAvailable ? (
          <>
            <div>
              {feature.payingTenants} paying · Est. MRR{" "}
              {formatCurrency(feature.estimatedMrrCents, currency)}
            </div>
            <div>
              Collected {formatCurrency(feature.collectedAllTimeCents, currency)}
              {mapMetric === GEOGRAPHY_MAP_METRICS.COLLECTED
                ? ` (${formatPercent(feature.revenueSharePct)} of platform)`
                : ""}
            </div>
            {feature.collectedLast30DaysCents ? (
              <div>
                Collected 30d {formatCurrency(feature.collectedLast30DaysCents, currency)}
              </div>
            ) : null}
            {feature.outstandingCents ? (
              <div>Outstanding {formatCurrency(feature.outstandingCents, currency)}</div>
            ) : null}
          </>
        ) : null}
      </div>
    </div>
  );
};

const TenantsGeographyView = ({
  geography,
  stateGeography = null,
  stateGeographyLoading = false,
  selectedStateCode = null,
  onSelectState,
  onClearState,
  loading = false,
  canListTenants = false,
  canViewBilling = false,
}) => {
  const navigate = useNavigate();
  const [hoveredCode, setHoveredCode] = useState(null);
  const [focusedCode, setFocusedCode] = useState(null);
  const [mapMetric, setMapMetric] = useState(GEOGRAPHY_MAP_METRICS.TENANTS);

  const revenueAvailable = Boolean(geography?.revenue_available && canViewBilling);
  const currency = geography?.currency || "AUD";

  const mapMetricOptions = useMemo(
    () =>
      revenueAvailable
        ? GEOGRAPHY_MAP_METRIC_OPTIONS
        : GEOGRAPHY_MAP_METRIC_OPTIONS.filter(
            (option) => option.id === GEOGRAPHY_MAP_METRICS.TENANTS
          ),
    [revenueAvailable]
  );

  const features = useMemo(
    () =>
      buildAustraliaMapFeatures(geography, {
        metric: mapMetric,
        revenueAvailable,
      }),
    [geography, mapMetric, revenueAvailable]
  );

  const metricMax = useMemo(
    () => getGeographyMetricMax(geography, mapMetric),
    [geography, mapMetric]
  );

  const legendSteps = useMemo(
    () =>
      buildTenantGeographyLegendSteps(metricMax, {
        revenue: mapMetric !== GEOGRAPHY_MAP_METRICS.TENANTS,
      }),
    [mapMetric, metricMax]
  );

  const breakdownRows = useMemo(() => buildCompleteStateBreakdownRows(geography), [geography]);
  const revenueTotals = geography?.revenue_totals || {};

  const activeFeature =
    features.find((feature) => feature.code === (hoveredCode || focusedCode)) || null;

  const legendTitle =
    mapMetric === GEOGRAPHY_MAP_METRICS.TENANTS
      ? "Tenants"
      : mapMetric === GEOGRAPHY_MAP_METRICS.MRR
        ? "Est. MRR"
        : "Collected";

  if (loading) {
    return <GeographySkeleton />;
  }

  if (selectedStateCode) {
    return (
      <TenantsGeographyStateView
        stateGeography={stateGeography}
        loading={stateGeographyLoading}
        canListTenants={canListTenants}
        canViewBilling={canViewBilling}
        mapMetricOptions={mapMetricOptions}
        mapMetric={mapMetric}
        onMapMetricChange={setMapMetric}
        onBack={onClearState}
      />
    );
  }

  if (!geography) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white px-4 py-8 text-center text-sm text-gray-500">
        Geography data is unavailable right now.
      </div>
    );
  }

  if (!geography.states?.length) {
    return null;
  }

  return (
    <section className="mb-4 rounded-xl border border-gray-200 bg-white p-4">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">Tenant & revenue geography</h2>
          <p className="mt-0.5 text-xs text-gray-500">
            {formatTenantGeographySummary(geography)}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {mapMetricOptions.length > 1 ? (
            <MetricToggle options={mapMetricOptions} value={mapMetric} onChange={setMapMetric} />
          ) : null}
          {canListTenants ? (
            <Link
              to="/tenants"
              className="text-xs font-medium text-indigo-600 hover:text-indigo-700"
            >
              Directory →
            </Link>
          ) : null}
        </div>
      </div>

      {revenueAvailable ? (
        <div className="mb-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
          {[
            {
              label: "Est. MRR",
              value: formatCurrency(revenueTotals.estimated_mrr_cents, currency),
            },
            {
              label: "Collected (all)",
              value: formatCurrency(revenueTotals.collected_all_time_cents, currency),
            },
            {
              label: "Collected (30d)",
              value: formatCurrency(revenueTotals.collected_last_30_days_cents, currency),
            },
            {
              label: "Outstanding",
              value: formatCurrency(revenueTotals.outstanding_cents, currency),
            },
            {
              label: "Paying tenants",
              value: String(revenueTotals.paying_tenants ?? 0),
            },
          ].map((item) => (
            <div
              key={item.label}
              className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2"
            >
              <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                {item.label}
              </div>
              <div className="mt-0.5 text-sm font-semibold text-gray-900">{item.value}</div>
            </div>
          ))}
        </div>
      ) : null}

      {canListTenants ? (
        <p className="mb-3 text-xs text-gray-500">
          Click a state to zoom in and see tenant counts by suburb/postcode area.
        </p>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
        <div className="relative overflow-hidden rounded-lg border border-gray-100 bg-slate-50">
          <svg
            viewBox={AUSTRALIA_MAP_VIEWBOX}
            className="h-auto w-full"
            role="img"
            aria-label="Heatmap of tenants and revenue by Australian state"
          >
            {features.map((feature) => {
              const isActive = feature.code && feature.code === (hoveredCode || focusedCode);
              const isInteractive = canListTenants && feature.code && feature.path;

              return (
                <g
                  key={feature.code || feature.name}
                  role={isInteractive ? "button" : undefined}
                  tabIndex={isInteractive ? 0 : undefined}
                  className={isInteractive ? "cursor-pointer" : undefined}
                  onClick={() => {
                    if (isInteractive && onSelectState) {
                      onSelectState(feature.code);
                    }
                  }}
                  onKeyDown={(event) => {
                    if (
                      isInteractive &&
                      onSelectState &&
                      (event.key === "Enter" || event.key === " ")
                    ) {
                      event.preventDefault();
                      onSelectState(feature.code);
                    }
                  }}
                  onMouseEnter={() => feature.code && setHoveredCode(feature.code)}
                  onMouseLeave={() => setHoveredCode(null)}
                  onFocus={() => feature.code && setFocusedCode(feature.code)}
                  onBlur={() => setFocusedCode(null)}
                >
                  <path
                    d={feature.path}
                    fill={feature.fill}
                    stroke={isActive ? "#312E81" : "#FFFFFF"}
                    strokeWidth={isActive ? 2 : 1}
                    opacity={hoveredCode && feature.code !== hoveredCode ? 0.72 : 1}
                    pointerEvents="all"
                  />
                </g>
              );
            })}

            {features.map((feature) =>
              feature.label && (feature.metricValue || feature.count) ? (
                <text
                  key={`${feature.code}-label`}
                  x={feature.label.x}
                  y={feature.label.y}
                  textAnchor="middle"
                  className="pointer-events-none select-none fill-gray-700 text-[11px] font-semibold"
                >
                  {feature.code}
                </text>
              ) : null
            )}
          </svg>

          <StateTooltip
            feature={activeFeature}
            mapMetric={mapMetric}
            currency={currency}
            revenueAvailable={revenueAvailable}
          />

          <div className="absolute right-3 top-3 rounded-lg border border-gray-200 bg-white/95 px-2 py-2 text-[10px] shadow-sm">
            <div className="mb-1 font-semibold uppercase tracking-wide text-gray-400">
              {legendTitle}
            </div>
            <div className="space-y-1">
              {legendSteps.map((step) => (
                <div key={step.label} className="flex items-center gap-2 text-gray-600">
                  <span
                    className="inline-block h-3 w-3 rounded-sm border border-white"
                    style={{ backgroundColor: step.color }}
                  />
                  <span>{step.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div>
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
            Tenants by state
          </div>
          <div className="max-h-[28rem] overflow-auto rounded-lg border border-gray-100">
            <table className="min-w-full divide-y divide-gray-100 text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-400">
                <tr>
                  <th className="px-3 py-2 font-semibold">State</th>
                  <th className="px-3 py-2 font-semibold text-right">Total</th>
                  <th className="px-3 py-2 font-semibold text-right">Share</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {breakdownRows.map((state) => (
                  <tr
                    key={state.code}
                    className={
                      canListTenants && state.code ? "cursor-pointer hover:bg-indigo-50/60" : ""
                    }
                    onMouseEnter={() => setHoveredCode(state.code)}
                    onMouseLeave={() => setHoveredCode(null)}
                    onClick={() => {
                      if (canListTenants && state.code && onSelectState) {
                        onSelectState(state.code);
                      }
                    }}
                  >
                    <td className="px-3 py-2 font-medium text-gray-900">
                      {state.code}
                      <span className="ml-2 hidden text-xs font-normal text-gray-500 sm:inline">
                        {state.name}
                      </span>
                      {canListTenants && state.code ? (
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            navigate(buildTenantsListPathForState(state.code));
                          }}
                          className="ml-2 text-xs font-normal text-indigo-600 hover:text-indigo-700"
                        >
                          directory
                        </button>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-gray-900">
                      {state.total}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-gray-500">
                      {formatPercent(state.tenant_share_pct)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="mt-4">
        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
          Complete breakdown by state
        </div>
        <div className="overflow-auto rounded-lg border border-gray-100">
          <table className="min-w-full divide-y divide-gray-100 text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-400">
              <tr>
                <th className="px-3 py-2 font-semibold">State</th>
                <th className="px-3 py-2 font-semibold text-right">Tenants</th>
                <th className="px-3 py-2 font-semibold text-right">Active</th>
                <th className="px-3 py-2 font-semibold text-right">Trial</th>
                <th className="px-3 py-2 font-semibold text-right">Suspended</th>
                <th className="px-3 py-2 font-semibold text-right">Share</th>
                {revenueAvailable ? (
                  <>
                    <th className="px-3 py-2 font-semibold text-right">Paying</th>
                    <th className="px-3 py-2 font-semibold text-right">Est. MRR</th>
                    <th className="px-3 py-2 font-semibold text-right">Collected</th>
                    <th className="px-3 py-2 font-semibold text-right">30d</th>
                    <th className="px-3 py-2 font-semibold text-right">Outstanding</th>
                    <th className="px-3 py-2 font-semibold text-right">Rev. share</th>
                  </>
                ) : null}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {breakdownRows.map((state) => (
                <tr
                  key={`full-${state.code}`}
                  className={
                    canListTenants && state.code ? "cursor-pointer hover:bg-indigo-50/60" : ""
                  }
                  onMouseEnter={() => setHoveredCode(state.code)}
                  onMouseLeave={() => setHoveredCode(null)}
                  onClick={() => {
                    if (canListTenants && state.code && onSelectState) {
                      onSelectState(state.code);
                    }
                  }}
                >
                  <td className="px-3 py-2 font-medium text-gray-900">
                    {state.code}
                    <span className="ml-2 hidden text-xs font-normal text-gray-500 lg:inline">
                      {state.name}
                    </span>
                    {canListTenants && state.code ? (
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          navigate(buildTenantsListPathForState(state.code));
                        }}
                        className="ml-2 text-xs font-normal text-indigo-600 hover:text-indigo-700"
                      >
                        directory
                      </button>
                    ) : null}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{state.total}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-600">
                    {state.active}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-600">
                    {state.on_trial || 0}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-600">
                    {state.suspended || 0}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-500">
                    {formatPercent(state.tenant_share_pct)}
                  </td>
                  {revenueAvailable ? (
                    <>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-600">
                        {state.paying_tenants || 0}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-900">
                        {formatCurrency(state.estimated_mrr_cents, currency)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-900">
                        {formatCurrency(state.collected_all_time_cents, currency)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-600">
                        {formatCurrency(state.collected_last_30_days_cents, currency)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-600">
                        {formatCurrency(state.outstanding_cents, currency)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-500">
                        {formatPercent(state.revenue_share_pct)}
                      </td>
                    </>
                  ) : null}
                </tr>
              ))}
              {revenueAvailable ? (
                <tr className="bg-gray-50 font-semibold text-gray-900">
                  <td className="px-3 py-2">Total</td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {breakdownRows.reduce((sum, state) => sum + (state.total || 0), 0)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {breakdownRows.reduce((sum, state) => sum + (state.active || 0), 0)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {breakdownRows.reduce((sum, state) => sum + (state.on_trial || 0), 0)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {breakdownRows.reduce((sum, state) => sum + (state.suspended || 0), 0)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">100%</td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {revenueTotals.paying_tenants || 0}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatCurrency(revenueTotals.estimated_mrr_cents, currency)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatCurrency(revenueTotals.collected_all_time_cents, currency)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatCurrency(revenueTotals.collected_last_30_days_cents, currency)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatCurrency(revenueTotals.outstanding_cents, currency)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">100%</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      {(geography.unmapped_tenants || 0) > 0 ? (
        <p className="mt-3 text-xs text-amber-700">
          {geography.unmapped_tenants} tenant
          {geography.unmapped_tenants === 1 ? "" : "s"} could not be mapped to a state.
        </p>
      ) : null}
    </section>
  );
};

export default TenantsGeographyView;
